import { Injectable } from '@angular/core';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { Sentiments } from '../enums/sentiments.enum';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { Field } from '@sb-tools/typescript/models/field.model';
import { ClientField } from '../models/client-field.model';

@Injectable({
    providedIn: 'root',
})
export class FormFieldsValidationService {
    sentimentsEnum = Sentiments;

    checkAndSetValidValues(field: Field | ClientField, checkedValue: string): void {
        switch (field.type) {
            case FieldType.accountNumber: {
                if (this.isAccountNumberValid(checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.address: {
                const addressValueArray = checkedValue.split(',');
                const zipValue = addressValueArray[0];
                const addressValue = addressValueArray[1];
                field.valueModel = {};

                if (!isNaN(+zipValue)) {
                    field.valueModel.zipCode = zipValue.trim();
                }

                if (addressValue) {
                    field.valueModel.address = addressValue.trim();
                }

                if (field.valueModel.zipCode || field.valueModel.address) {
                    field.value =
                        (field.valueModel.zipCode || '') +
                            (field.valueModel.address ? ', ' : '') +
                            field.valueModel.address || '';
                }

                break;
            }
            case FieldType.attachment:
            case FieldType.storeInfo:
            case FieldType.formHeader: {
                break;
            }
            case FieldType.sentiment: {
                const isValueCorrect = checkedValue.toString().toLowerCase() in this.sentimentsEnum;

                if (!isValueCorrect) {
                    field.value = this.sentimentsEnum.question;
                } else {
                    if (isNaN(+checkedValue)) {
                        field.value = this.sentimentsEnum[checkedValue.toLowerCase()];
                    } else {
                        field.value = +checkedValue;
                    }
                }

                break;
            }
            case FieldType.select:
            case FieldType.region:
            case FieldType.checklist:
            case FieldType.radio: {
                const correctValuesList = checkedValue.split(', ').filter((value) => {
                    return this.isValueInPossibleValuesList(field, value);
                });

                if (correctValuesList.length) {
                    field.value = correctValuesList.join(', ');
                }

                break;
            }
            case FieldType.checkbox: {
                if (['false', 'true'].includes(checkedValue)) {
                    field.value = !(checkedValue === 'false');
                }
                break;
            }
            case FieldType.emailInput:
            case FieldType.outboundEmailAddress: {
                if (HelperService.isEmailValid(checkedValue)) {
                    field.value = checkedValue;
                }

                break;
            }
            case FieldType.mobileNumber: {
                if (this.isMobileNumberValid(checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.numberInput: {
                if (this.isNumberValueValid(field, checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.textInput: {
                if (this.isTextInputValueValid(field, checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.starRating: {
                if (this.isStarRatingValueValid(field, Number(checkedValue))) {
                    field.value = checkedValue;
                }
                break;
            }
            default: {
                field.value = checkedValue;
            }
        }
    }

    isAccountNumberValid(value: string): boolean {
        // example of valid account: 97135974068
        const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
        const numberWithoutSpacesAndPeriods = value.replace(/[\s.]+/g, '');

        if (numberWithoutSpacesAndPeriods.length !== 11) {
            return false;
        } else {
            const checkDigit = parseInt(numberWithoutSpacesAndPeriods.charAt(10), 10);
            const accountNumberWithoutCheckDigit = numberWithoutSpacesAndPeriods.substring(0, 10);
            let sum = 0;

            for (let index = 0; index < 10; index++) {
                sum += parseInt(accountNumberWithoutCheckDigit.charAt(index), 10) * weights[index];
            }
            const remainder = sum % 11;
            return checkDigit === (remainder === 0 ? 0 : 11 - remainder);
        }
    }

    isRequiredValuesFilled(fields: (ClientField | Field)[]): boolean {
        const checkedFormElementTypes = [
            FieldType.attachment,
            FieldType.categories,
            FieldType.checklist,
            FieldType.date,
            FieldType.ccUsers,
            FieldType.bccUsers,
        ];

        let isValid = true;
        checkedFormElementTypes.forEach((type) => {
            fields.find((formField) => {
                if (formField.type === type && this.isNeedToCheckFieldValue(formField)) {
                    if (formField.type === FieldType.categories) {
                        isValid = !formField.isRequired || !!formField.value;
                    } else if (formField.isRequired && !(formField.value && formField.value.length)) {
                        isValid = false;
                    }
                }
            });
        });

        return isValid;
    }

    hasMissingRequiredForTeamFields(fields: (ClientField | Field)[]): boolean {
        return this.getMissingRequiredForTeamFields(fields).length > 0;
    }

    getMissingRequiredForTeamFields(fields: (ClientField | Field)[]): (ClientField | Field)[] {
        return fields.filter((field) => {
            return (
                field.isRequiredForTeam && this.isNeedToCheckFieldValue(field) && this.isFieldValueMissing(field.value)
            );
        });
    }

    isValueInPossibleValuesList(formField: Field | ClientField, value: string): boolean {
        return !!formField.possibleValues.find((possibleValue) => {
            return possibleValue.value === value;
        });
    }

    scrollToInvalidField(formIdSelector: string, scrolledContainerIdSelector: string, offsetTop = 0): void {
        const invalidElement = document.querySelector(`${formIdSelector} .ng-invalid`) as HTMLElement;
        if (!invalidElement) {
            return;
        }

        const scrolledContainer = document.querySelector(scrolledContainerIdSelector) as HTMLElement;
        const scrollTarget = invalidElement.offsetTop - 30 - offsetTop;
        scrolledContainer.scrollTo({ top: scrollTarget, behavior: 'smooth' });

        const focusWhenDone = () => {
            const isScrolling = Math.abs(scrolledContainer.scrollTop - scrollTarget) > 1;

            if (isScrolling) {
                requestAnimationFrame(focusWhenDone);
            } else {
                const quillEditor = invalidElement.querySelector('.quill-editor.ng-invalid .ql-editor') as HTMLElement;
                if (quillEditor) {
                    quillEditor.focus();
                    return;
                }

                const formControl = invalidElement.querySelector('.form-control.ng-invalid') as HTMLElement;
                if (formControl) {
                    formControl.focus();
                    return;
                }

                const timepicker = invalidElement.querySelector('.bs-timepicker-field') as HTMLElement;
                if (timepicker) {
                    timepicker.focus();
                    return;
                }
            }
        };
        requestAnimationFrame(focusWhenDone);
    }

    private isMobileNumberValid(value: string): boolean {
        return /^(\+)?[0-9]{4,13}$/.test(value);
    }

    private isNeedToCheckFieldValue(formField: ClientField | Field): boolean {
        return !formField.isDependant || (formField.isDependant && formField.isShowedBecauseOfRule);
    }

    private isFieldValueMissing(value: unknown): boolean {
        if (value == null) {
            return true;
        }

        if (Array.isArray(value)) {
            return value.length === 0;
        }

        if (typeof value === 'string') {
            return value.trim().length === 0;
        }

        return false;
    }

    private isNumberValueValid(field: Field | ClientField, value: string): boolean {
        const valueWithoutComma = value.replace(/,/g, '.');
        if (isNaN(Number(valueWithoutComma)) || isNaN(parseFloat(valueWithoutComma))) {
            return false;
        }

        const maxLength = field.options?.maxNumber ? +field.options?.maxNumber : Number.MAX_VALUE;
        if (value.length > maxLength) {
            return false;
        }

        const minLength = field.options?.minNumber != null ? +field.options?.maxNumber : 0;
        if (value.length < minLength) {
            return false;
        }

        return true;
    }

    private isTextInputValueValid(field: Field | ClientField, value: string): boolean {
        return field.options.pattern ? new RegExp(field.options.pattern).test(value) : true;
    }

    private isStarRatingValueValid(field: Field | ClientField, value: number): boolean {
        return value > 0 && value <= field.options.maxStarCount;
    }
}
